import { isEmpty } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  getCandidateListThunk,
  getCandidateMetaThunk,
} from '../../../redux/Candidate/Candidate.thunk';
import {
  setHeaderSubTitle,
  setHeaderTabs,
  setHeaderTitle,
} from '../../../redux/App/App.slice';
import {
  getCandidatesList,
  getCandidatesMeta,
  resetCandidateSelectedAction,
} from '../../../redux/Candidate/Candidate.slice';
import { getAssistantMessages, getAssistantMiniChatOpen, setAssistantMiniChatOpenAction } from '../../../redux/Assistant/Assistant.slice';
import { useAppDispatch } from '../../../hooks/useAppDispatcher.hook';
import { useAppSelector } from '../../../hooks/useAppSelector.hook';
import { useModalManager } from '../../../hooks/ModalManager.hook';
import useAuthHook from '../../../hooks/useAuth.hook';

import {
  addToQueryString,
  getKeywords,
  getPage,
  getQueryStringFrom,
  getSort,
  parseSearchParamsRouter,
} from '../../../services/router.service';
import {
  checkUserRequiredPermissions,
  checkUserRequiredRole,
} from '../../../utils/getUserRolesByToken';
import { UserPermissions } from '../../../types/userPermissions.types';
import { SortTypes } from '../../../types/sort.types';
import { Region } from '../../../redux/Region/Region.type';

import TableComponent from '../../../components/Table/Table.component';
import { TableHeaderItemOrderProps } from '../../../components/Table/TableHeader.component';
import CardComponent from '../../../components/Card.component';
import BadgeComponent from '../../../components/Badge.component';

import FloatButtonComponent from '../../../components/FloatButton.component';
import PersonEditModalComponent from '../../../components/Modal/Candidate/PersonEditModal.component';
import CandidatesFiltersForm from '../../forms/Candidates/Filters.form';
import CandidatesMiniChat from './components/CandidatesMiniChat/CandidatesMiniChat.component';

import { RoutesConstant } from '../../../constants/routes.constant';

import PlusIcon from '../../../components/Icons/Plus.icon';
import MessageIcon from '../../../components/Icons/Message.icon';
import { MINI_CHAT_TRANSITION_MS } from './components/CandidatesMiniChat/CandidatesMiniChat.styles';
import {
  StyledAvailableRegions,
  StyledCandidatesCardBody,
  StyledCandidatesPage,
  StyledCandidatesTablePane,
  StyledCandidatesToolbar,
  StyledMiniChatSlot,
  StyledMiniChatToggleBadge,
  StyledMiniChatToggleButton,
  StyledTableCreateButton,
} from './Candidates.styled';
import { CANDIDATES_GROUPS } from '../../../types/candidates.types';
import {
  CREATE_ICON_SIZE,
  TABLE_HEADER_COLUMNS,
} from './constants/constants';
import { CandidatesTableRow } from './components/CandidatesTableRow';
import { UserRole } from '../../../types/userRole.types';

const CandidatesPage = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const meta = useAppSelector(getCandidatesMeta);
  const list = useAppSelector(getCandidatesList);
  const [, setSearchParams] = useSearchParams();
  const { group } = useParams();
  const { openModal } = useModalManager();
  const { user } = useAuthHook();
  const userRoleIds = user.regions.map((item: any) => item?.id);

  const isHeadUser = checkUserRequiredRole(user.roles, [
    UserRole.HEAD,
    UserRole.DEV_HEAD,
  ]);

  useEffect(() => {
    dispatch(resetCandidateSelectedAction(undefined));

    if (!group) return;

    if (group === CANDIDATES_GROUPS.ARCHIVE) {
      dispatch(setHeaderTitle('Archive'));
    } else if (group === CANDIDATES_GROUPS.FAVOURITE) {
      dispatch(setHeaderTitle('Favourites'));
    }
    else if (group === CANDIDATES_GROUPS.SEARCH) {
      dispatch(setHeaderTitle('Search candidates'));
    } else {
      dispatch(setHeaderTitle('Candidates'));
    }

    if (!meta.items) return;

    let headerTabs = [
      {
        path: RoutesConstant.CANDIDATES_GROUPS.BASE,
        text: 'header.tabs.candidates.base',
        badge: meta.items.BASE_COUNTER,
      },
      {
        path: RoutesConstant.CANDIDATES_GROUPS.COMMUNICATION,
        text: 'header.tabs.candidates.communication',
        badge: meta.items.COMMUNICATION_COUNTER,
      },
      {
        path: RoutesConstant.CANDIDATES_GROUPS.REQUESTS,
        text: 'header.tabs.candidates.requests',
        badge: meta.items.REQUESTS_COUNTER,
      },
      {
        path: RoutesConstant.CANDIDATES_GROUPS.WAIT,
        text: 'header.tabs.candidates.wait',
        badge: meta.items.WAIT_COUNTER,
      },
      {
        path: RoutesConstant.CANDIDATES_GROUPS.WORK,
        text: 'header.tabs.candidates.other',
        badge: meta.items.WORK_COUNTER,
      },
    ];

    if (isHeadUser) {
      headerTabs = headerTabs.filter(item => item.path !== RoutesConstant.CANDIDATES_GROUPS.REQUESTS);
    }

    dispatch(setHeaderTabs(headerTabs));

    return () => {
      dispatch(setHeaderSubTitle(undefined));
      dispatch(setHeaderTabs([]));
    };
  }, [dispatch, group, meta]);

  useEffect(() => {
    const savedPage = Number(sessionStorage.getItem("candidatesPage")) || 1;
    const savedSort = sessionStorage.getItem("candidatesSort") || "-date";

    // Если таба изменилась — сбрасываем на 1 страницу и дефолтную сортировку
    if (!sessionStorage.getItem("group") || sessionStorage.getItem("group") !== group) {
      sessionStorage.setItem("group", group as string);
      sessionStorage.removeItem("candidatesPage");
      sessionStorage.removeItem("candidatesSort");
      handleChangePage(1, "-date");
    } else {
      handleChangePage(savedPage, savedSort);
    }
  }, [group]);

  const isSearchMode = useMemo(() => group === 'search', [group]);

  const baseFilters = useMemo(() => {
    const params = parseSearchParamsRouter(window.location.search);
    delete params['keywords'];
    delete params['page'];
    delete params['sort'];
    return {
      filter: params || {},
    };
  }, [window.location.search]);

  const keywords = useMemo(() => {
    return getKeywords(window.location.search);
  }, [window.location.search]);

  const currentPage: number = useMemo(() => {
    return getPage(window.location.search) || 1;
  }, [group, window.location.search]);

  const currentSort: string = useMemo(() => {
    return getSort(window.location.search) || '-date';
  }, [group, window.location.search]);

  const handleGetList = (
    page: number,
    sort?: string,
    filters: any = undefined,
  ) => {
    dispatch(getCandidateMetaThunk(userRoleIds));
    if (!group) return;
    const _keywords = filters
      ? filters.keywords
        ? filters.keywords
        : ''
      : keywords;
    dispatch(
      getCandidateListThunk({
        search: _keywords,
        tab: group?.toUpperCase(),
        region: !isSearchMode
          ? user?.regions.map((region: Region) => region.id)
          : undefined,
        page,
        sort,
        ...(filters || baseFilters),
      }),
    );
  };

  const handleChangePage = (page: number, sort?: string | null) => {
    if (!group) {
      return;
    }

    sessionStorage.setItem('candidatesPage', String(page));

    const search = addToQueryString([
      { name: 'page', value: page },
      { name: 'sort', value: sort || currentSort },
    ]);
    setSearchParams(search, { replace: true });
    handleGetList(page, sort || currentSort);
  };

  const handleChangeSort = ({
    sort,
    dataProperty,
  }: TableHeaderItemOrderProps) => {
    if (!group) return;
    const isCurrentSort = currentSort.includes(dataProperty);
    const isDesc = currentSort.startsWith('-');

    const newSortDirection = isCurrentSort && isDesc ? SortTypes.ASC : SortTypes.DESC;

    const sortDirective = newSortDirection === SortTypes.DESC ? '-' : '';
    const sortComplex = `${sortDirective}${dataProperty}`;
    sessionStorage.setItem('candidatesSort', sortComplex);

    const search = addToQueryString([{
      name: 'sort',
      value: sortComplex,
    }]);
    setSearchParams(search, { replace: true });
    handleGetList(currentPage, sortComplex);
  };

  const handleChangeFilters = (filters: any) => {
    if (!group) return;
    const queryFilters = Object.keys(filters.filter).map((filter: any) => {
      return { name: filter, value: filters.filter[filter] };
    });
    const search = getQueryStringFrom([
      {
        name: 'keywords',
        value: filters.keywords,
      },
      ...queryFilters,
      { name: 'page', value: 1 },
    ]);

    setSearchParams(search, { replace: true });
    handleGetList(1, currentSort, filters);
  };

  const [isFavouriteChanged, setFavouriteChanged] = useState(false);
  const isMiniChatOpen = useAppSelector(getAssistantMiniChatOpen);
  const messages = useAppSelector(getAssistantMessages);

  const canViewAssistant = checkUserRequiredPermissions(user.permissions, [
    UserPermissions.CAN_VIEW_CANDIDATE,
  ]);
  const canCreateCandidate = checkUserRequiredPermissions(user.permissions, [
    UserPermissions.CAN_CREATE_CANDIDATE,
  ]);
  const isMiniChatVisible = canViewAssistant && isMiniChatOpen;
  const [isMiniChatRendered, setIsMiniChatRendered] =
    useState(isMiniChatVisible);
  const [isMiniChatExpanded, setIsMiniChatExpanded] =
    useState(isMiniChatVisible);

  useEffect(() => {
    if (isMiniChatVisible) {
      setIsMiniChatRendered(true);
      return;
    }

    setIsMiniChatExpanded(false);
  }, [isMiniChatVisible]);

  useEffect(() => {
    if (!isMiniChatRendered || !isMiniChatVisible) {
      return;
    }

    const frame = requestAnimationFrame(() => {
      setIsMiniChatExpanded(true);
    });

    return () => cancelAnimationFrame(frame);
  }, [isMiniChatRendered, isMiniChatVisible]);

  useEffect(() => {
    if (isMiniChatVisible || !isMiniChatRendered) {
      return;
    }

    const timeout = window.setTimeout(() => {
      setIsMiniChatRendered(false);
    }, MINI_CHAT_TRANSITION_MS + 50);

    return () => window.clearTimeout(timeout);
  }, [isMiniChatRendered, isMiniChatVisible]);

  const handleMiniChatTransitionEnd = (
    event: React.TransitionEvent<HTMLDivElement>,
  ) => {
    if (event.target !== event.currentTarget) {
      return;
    }

    if (event.propertyName !== 'width' && event.propertyName !== 'height') {
      return;
    }

    if (!isMiniChatVisible) {
      setIsMiniChatRendered(false);
    }
  };

  const handleToggleMiniChat = () => {
    dispatch(setAssistantMiniChatOpenAction(!isMiniChatOpen));
  };

  return (
    <StyledCandidatesPage filterBlock={isSearchMode}>
      {isSearchMode && (
        <CandidatesFiltersForm
          onApply={(filters: any) => handleChangeFilters(filters)}
          userRoleIds={userRoleIds}
        />
      )}
      <CardComponent>
        <StyledCandidatesToolbar>
          {!isSearchMode && !isEmpty(user.regions) && (
            <StyledAvailableRegions>
              <BadgeComponent
                appearance="warning"
                text={user.regions
                  .map((region: Region) => region.name)
                  .join(', ')}
              />
            </StyledAvailableRegions>
          )}
          {canViewAssistant && (
            <StyledMiniChatToggleButton
              type="button"
              $active={isMiniChatOpen}
              onClick={handleToggleMiniChat}
            >
              <MessageIcon width={14} height={14} />
              {t('assistant.miniChatToggle')}
              {!isMiniChatOpen && messages.length > 0 && (
                <StyledMiniChatToggleBadge />
              )}
            </StyledMiniChatToggleButton>
          )}
        </StyledCandidatesToolbar>
        <StyledCandidatesCardBody>
          <StyledCandidatesTablePane
            hasBadge={!isSearchMode && !isEmpty(user.regions)}
          >
            <TableComponent
              header={
                checkUserRequiredRole(user.roles, [
                  UserRole.HEAD,
                  UserRole.DEV_HEAD,
                ])
                  ? TABLE_HEADER_COLUMNS
                  : TABLE_HEADER_COLUMNS.filter(
                      col => col.dataProperty !== 'isFavourite',
                    )
              }
              loading={isFavouriteChanged ? false : list.loading}
              pagination={{
                ...list.pagination,
                currentPage,
              }}
              onSortChange={handleChangeSort}
              onPageChange={handleChangePage}
              isFavouriteChanged={isFavouriteChanged}
              setFavouriteChanged={setFavouriteChanged}
            >
              <CandidatesTableRow
                handleGetList={() => handleGetList(currentPage, currentSort)}
                list={list}
                openModal={openModal}
                permissions={user.permissions}
                setFavouriteChanged={setFavouriteChanged}
              />
            </TableComponent>
            {canCreateCandidate && (
              <StyledTableCreateButton>
                <FloatButtonComponent
                  onClick={() =>
                    openModal(
                      PersonEditModalComponent,
                      {},
                      { disableClose: false },
                    )
                  }
                >
                  <PlusIcon width={CREATE_ICON_SIZE} height={CREATE_ICON_SIZE} />
                </FloatButtonComponent>
              </StyledTableCreateButton>
            )}
          </StyledCandidatesTablePane>
          {isMiniChatRendered && (
            <StyledMiniChatSlot
              $open={isMiniChatExpanded}
              onTransitionEnd={handleMiniChatTransitionEnd}
            >
              <CandidatesMiniChat onClose={handleToggleMiniChat} />
            </StyledMiniChatSlot>
          )}
        </StyledCandidatesCardBody>
      </CardComponent>
    </StyledCandidatesPage>
  );
};

export default CandidatesPage;
